<script>

// This code was adapted from:
// https://github.com/MadimetjaShika/vuetify-google-autocomplete
// which was itself adapted from:
// https://github.com/olefirenko/vue-google-autocomplete

import { VTextField } from 'vuetify/lib'

import { Loader } from '@googlemaps/js-api-loader'

export default {
  name: 'fe-google-autocomplete',

  extends: VTextField,

  props: {
    //  Select Address Types and Address Component Types
    //  https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
    
    addressComponents: {
      type: Object,
      default: () => ({
        street_number: 'short_name',
        route: 'long_name',
        locality: 'long_name',
        administrative_area_level_1: 'short_name',
        administrative_area_level_2: 'short_name',
        country: 'long_name',
        postal_code: 'short_name',
      }),
    },

    selectionRequired: {
      type: Boolean,
      default: false,
    },
    
    selectionRequiredText: {
      type: String,
      default: 'Please select an address from the list.',
    },
    
    /**
     * Types supported in place autocomplete requests.
     *
     * @alias module:vuetify-google-autocomplete.props.types
     * @see {@link https://developers.google.com/places/supported_types#table3}
     * @see {@link https://developers.google.com/places/web-service/autocomplete}
     * @type {String}
     */
    types: {
      type: String,
      default: 'address',
    },
  },

  data: () => ({
    /**
     * The Autocomplete object.
     * @access private
     * @type {Autocomplete}
     * @link https://developers.google.com/maps/documentation/javascript/reference#Autocomplete
     */
    gAutocomplete: null,

    /**
     * Autocomplete input text
     * @access private
     * @type {String}
     */
    autocompleteText: '',

    /**
     * The address of the last place the user selected.
     * @access private
     * @type {String}
     */
    lastSelectedPlace: '',
  }),
  
  computed: {
    rulesPlusInternalRules() {
      const enforceSelectionRequired = []

      if (this.selectionRequired
        && (this.lastSelectedPlace.trim() === '' || this.lastSelectedPlace !== this.autocompleteText)) {
        enforceSelectionRequired.push(this.selectionRequiredText)
      } else {
        enforceSelectionRequired.push(true)
      }

      return [...this.rules, ...enforceSelectionRequired]
    },

  },

  methods: {
    onUpdateError() {
      this.$emit('update:error')
    },

    onChange() {
      this.$emit('change', this.autocompleteText)
    },

    clear() {
      this.autocompleteText = ''
      this.$refs.textField.clearableCallback()
    },

    update(value) {
      this.autocompleteText = value
    },

    async setupGoogle() {
      const loader = new Loader({
        apiKey: process.env.VUE_APP_GOOGLE_MAPS_KEY,
        version: 'weekly',
        libraries: ['places']
      })

      const options = {}

      if (this.types) { options.types = [this.types] }

      if (this.country) {
        options.componentRestrictions = {
          country: this.country,
        }
      }

      if (this.fields) {
        if (typeof this.fields === 'string') {
          options.fields = [this.fields]
        } else {
          options.fields = this.fields
        }
      }

      const inputEl = this.$el.querySelector('input')
      const { Autocomplete } = await loader.importLibrary('places')

      this.gAutocomplete = new Autocomplete(inputEl, options)
        
      // this insanity disables google from setting the autocomplete on the input to "off"
      window.google.maps.event.addDomListener( 
        inputEl,
        'focus',
        e => e.target.setAttribute('autocomplete', this.$attrs.autocomplete || 'new-password')
      )

      // this is potentially inside a promise so the autocomplete MUST exist to add the listener
      this.gAutocomplete.addListener('place_changed', () => {
        const place = this.gAutocomplete.getPlace()

        // if (!place || !place.geometry) {
        if (Object.keys(place).length < 2) {
          // User entered the name of a Place that was not suggested and
          // pressed the Enter key, or the Place Details request failed.
          this.$emit('no-results-found', place)
          return
        }

        const returnData = {}

        if (place.name !== undefined && this.placeName) {
          this.autocompleteText = place.name
        } else if (place.formatted_address !== undefined) {
          this.autocompleteText = place.formatted_address
        }

        if (place.address_components !== undefined) {

            // Get each component of the address from the place details
          place.address_components.forEach (component => {
            component.types.forEach (type => {
              if (this.addressComponents[type]) {
                returnData[type] = component[this.addressComponents[type]]
              }
            })
          })

          if (place.geometry) {
            returnData.latitude = place.geometry.location.lat()
            returnData.longitude = place.geometry.location.lng()
          }

          // additional fields available in google places results
          if (place.name) {
            returnData.name = place.name
          }

          if (place.place_id) {
            returnData.place_id = place.place_id
          }

          // return returnData object and PlaceResult object
          this.$emit('placechanged', returnData, place, this.id)

          // update autocompleteText then emit change event
          this.lastSelectedPlace = this.autocompleteText
          this.onChange()
          if (this.validateOnBlur) {
            // manually validate the underlying v-text-field because
            // selecting an option causes the field to lose focus
            // before the place_changed event is emitted and we have
            // a chance to fill the field with the formatted address
            this.$refs.textField.validate()
          }
        }
      })

      // Override the default placeholder
      // text set by Google with the
      // placeholder prop value or an empty value.
      inputEl.setAttribute('placeholder', this.placeholder ? this.placeholder : '')
    },
  },

  // Updates the autocompleteText member if a v-model was provided.
  created() {
    this.autocompleteText = this.value ? this.value : ''
    this.lastSelectedPlace = this.autocompleteText
  },

  mounted() {
    this.setupGoogle()
  },

  watch: {
    // Emit the new autocomplete text whenever it changes.
    autocompleteText(newVal) {
      this.$emit('input', newVal || '')
    },

    // Keep autocompleteText up-to-date with v-model.
    value(newVal) {
      if (newVal !== this.autocompleteText) {
        this.autocompleteText = newVal
        this.lastSelectedPlace = newVal
      }
    },

    // Update the SDK country option whenever it changes from the parent.
    country(newVal) {
      if (newVal) {
        this.gAutocomplete.componentRestrictions.country = newVal
      }
    },

    // Update the SDK fields option whenever it changes from the parent.
    fields(newVal) {
      if (newVal) {
        if (typeof newVal === 'string') {
          this.setFields([newVal])
        } else {
          this.setFields(newVal)
        }
      }
    },

    // Update the SDK types option whenever it changes from the parent.
    types(newVal) {
      if (newVal) {
        this.gAutocomplete.setTypes([this.types])
      }
    },
  },
}
</script>